/** BizOps / DB column limit for address line fields in online journey forms. */
export const ADDRESS_LINE_MAX_LENGTH = 30;

/**
 * Packs up to three address lines so each is at most {@link maxLen} characters.
 * Overflow from line N is prepended to line N+1.
 */
export function packAddressLines(lines, maxLen = ADDRESS_LINE_MAX_LENGTH) {
  const input = [
    String(lines?.[0] ?? "").trim(),
    String(lines?.[1] ?? "").trim(),
    String(lines?.[2] ?? "").trim(),
  ];
  const out = ["", "", ""];
  let overflow = "";

  for (let i = 0; i < 3; i += 1) {
    const combined = `${overflow}${input[i] || ""}`.trim();
    if (combined.length <= maxLen) {
      out[i] = combined;
      overflow = "";
    } else {
      out[i] = combined.slice(0, maxLen);
      overflow = combined.slice(maxLen);
    }
  }

  return out;
}

/** Maps Aadhaar `address` object to three packed lines (house/street, vtc, subdist|dist|loc). */
export function mapAadhaarToAddressLines(addr) {
  if (!addr || typeof addr !== "object") {
    return ["", "", ""];
  }
  const line1 = [addr.house, addr.street].filter(Boolean).join(" ");
  const line2 = String(addr.vtc || "").trim();
  const line3 = String(addr.subdist || addr.dist || addr.loc || "").trim();
  return packAddressLines([line1, line2, line3]);
}

export function packAddressFields(formData, keys) {
  if (!formData || typeof formData !== "object") {
    return formData;
  }
  const [line1, line2, line3] = packAddressLines([
    formData[keys[0]],
    formData[keys[1]],
    formData[keys[2]],
  ]);
  return {
    ...formData,
    [keys[0]]: line1,
    [keys[1]]: line2,
    [keys[2]]: line3,
  };
}

const LIFE_ASSURED_ADDRESS_KEYS = [
  "addressLine1",
  "addressLine2",
  "addressLine3",
];

const COMMUNICATION_ADDRESS_KEYS = [
  "communicationAddressLine1",
  "communicationAddressLine2",
  "communicationAddressLine3",
];

const NOMINEE_MAILING_ADDRESS_KEYS = [
  "mailingAddressLine1",
  "mailingAddressLine2",
  "mailingAddressLine3",
];

const NOMINEE_COMMUNICATION_ADDRESS_KEYS = [
  "communicationAddressLine1",
  "communicationAddressLine2",
  "communicationAddressLine3",
];

const APPOINTEE_ADDRESS_KEYS = [
  "appointeeAddressLine1",
  "appointeeAddressLine2",
  "appointeeAddressLine3",
];

export function normalizeLifeAssuredAddressFields(formData) {
  return packAddressFields(formData, LIFE_ASSURED_ADDRESS_KEYS);
}

export function normalizeCommunicationAddressFields(formData) {
  return packAddressFields(formData, COMMUNICATION_ADDRESS_KEYS);
}

/** Permanent + communication address lines on proposer / life assured forms. */
export function normalizeProposalAddressFields(formData) {
  return normalizeCommunicationAddressFields(
    normalizeLifeAssuredAddressFields(formData),
  );
}

/**
 * Proposer address keys to read for one address line/part, in priority order:
 * the proposer's own residential address first, then their communication
 * address (a resumed journey can carry only the latter).
 */
const PROPOSER_ADDRESS_SOURCE_KEYS = {
  AddressLine1: ["addressLine1", "communicationAddressLine1"],
  AddressLine2: ["addressLine2", "communicationAddressLine2"],
  AddressLine3: ["addressLine3", "communicationAddressLine3"],
  PinCode: ["pinCode", "communicationPinCode"],
  State: ["state", "communicationState"],
  City: ["city", "communicationCity"],
};

/** Nominee forms keep the same address under their own key names. */
const NOMINEE_ADDRESS_TARGET_KEYS = {
  mailingAddressLine1: PROPOSER_ADDRESS_SOURCE_KEYS.AddressLine1,
  mailingAddressLine2: PROPOSER_ADDRESS_SOURCE_KEYS.AddressLine2,
  mailingAddressLine3: PROPOSER_ADDRESS_SOURCE_KEYS.AddressLine3,
  pinCode: PROPOSER_ADDRESS_SOURCE_KEYS.PinCode,
  state: PROPOSER_ADDRESS_SOURCE_KEYS.State,
  city: PROPOSER_ADDRESS_SOURCE_KEYS.City,
};

const firstFilledValue = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (String(value ?? "").trim()) return value;
  }
  return "";
};

/**
 * The proposer's address written under the target form's own key names.
 * Returns null when the proposer has no address to copy, so callers can leave
 * the payload untouched rather than blanking it.
 */
const buildAddressFromProposer = (proposerData, targetKeys) => {
  if (!proposerData || typeof proposerData !== "object") return null;

  const address = {};
  let hasAnyValue = false;
  Object.entries(targetKeys).forEach(([targetKey, sourceKeys]) => {
    const value = firstFilledValue(proposerData, sourceKeys);
    address[targetKey] = value;
    if (String(value ?? "").trim()) hasAnyValue = true;
  });

  return hasAnyValue ? address : null;
};

/**
 * The proposer's address under an annuitant's own `pa*`/`sa*` address keys,
 * for "Use the same address as proposer": that toggle clears the annuitant's
 * address fields, so without this the payload would carry the flag and six
 * empty fields.
 */
export function buildAnnuitantAddressFromProposer(proposerData, prefix) {
  if (!prefix) return null;
  const targetKeys = Object.fromEntries(
    Object.entries(PROPOSER_ADDRESS_SOURCE_KEYS).map(([suffix, sourceKeys]) => [
      `${prefix}${suffix}`,
      sourceKeys,
    ]),
  );
  return buildAddressFromProposer(proposerData, targetKeys);
}

/**
 * The proposer's address under a nominee's own address keys, for the
 * nominee's "same address as proposer" toggle.
 */
export function buildNomineeAddressFromProposer(proposerData) {
  return buildAddressFromProposer(proposerData, NOMINEE_ADDRESS_TARGET_KEYS);
}

export function normalizeNomineeAddressFields(nominee) {
  if (!nominee || typeof nominee !== "object") {
    return nominee;
  }
  return packAddressFields(
    packAddressFields(
      packAddressFields(nominee, NOMINEE_MAILING_ADDRESS_KEYS),
      NOMINEE_COMMUNICATION_ADDRESS_KEYS,
    ),
    APPOINTEE_ADDRESS_KEYS,
  );
}

export function normalizeNomineesList(nominees) {
  if (!Array.isArray(nominees)) {
    return nominees;
  }
  return nominees.map((nominee) => normalizeNomineeAddressFields(nominee));
}

/**
 * Resolves address lines from persisted journey, Aadhaar, and lead — then packs to max length.
 */
export function resolveAddressLinesFromSources({
  persisted = {},
  aadhaarAddr,
  lead = {},
} = {}) {
  const [aad1, aad2, aad3] = mapAadhaarToAddressLines(aadhaarAddr);
  return packAddressLines([
    String(persisted.addressLine1 || "").trim() || aad1 || lead.addressLine1 || "",
    String(persisted.addressLine2 || "").trim() || aad2 || lead.addressLine2 || "",
    String(persisted.addressLine3 || "").trim() || aad3 || lead.addressLine3 || "",
  ]);
}
